import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

interface AdminStats {
  totalUsers: number;
  totalListings: number;
  pendingListings: number;
  activeListings: number;
  closedListings: number;
  totalApplications: number;
  totalChatSessions: number;
}

export function useAdmin() {
  const { user } = useAuth();

  // Check admin status with caching
  const { data: isAdmin, isLoading: adminStatusLoading } = useQuery({
    queryKey: ["admin-status", user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .single();

      if (error) return false;
      return data?.role === "admin";
    },
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000, // 10 minutes - admin status changes rarely
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  // Fetch admin stats only if user is admin
  const { data: stats, isLoading: statsLoading, refetch: refreshStats } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async (): Promise<AdminStats> => {
      // Use Promise.all for parallel requests to improve performance
      const [usersCount, jobs, applicationsCount, chatSessionsCount] = await Promise.all([
        supabase.from("profiles").select("*", { count: "exact", head: true }),
        supabase.from("jobs").select("status"),
        supabase.from("applications").select("*", { count: "exact", head: true }),
        supabase.from("chat_sessions").select("*", { count: "exact", head: true })
      ]);

      const jobsData = jobs.data || [];
      const totalListings = jobsData.length;
      const pendingListings = jobsData.filter((job) => job.status === "pending").length;
      const activeListings = jobsData.filter((job) => job.status === "active").length;
      const closedListings = jobsData.filter((job) => job.status === "closed").length;

      return {
        totalUsers: usersCount.count || 0,
        totalListings,
        pendingListings,
        activeListings,
        closedListings,
        totalApplications: applicationsCount.count || 0,
        totalChatSessions: chatSessionsCount.count || 0,
      };
    },
    enabled: isAdmin === true, // Only fetch stats if user is admin
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  const updateJobStatus = async (jobId: string, status: 'pending' | 'active' | 'closed') => {
    try {
      const { error } = await supabase.from('jobs').update({ status }).eq('id', jobId);
      if (error) throw error;
      
      await refreshStats(); // Refresh stats after update
      return { success: true };
    } catch (error) {
      console.error('Error updating job status:', error);
      return { success: false, error };
    }
  };

  return {
    isAdmin: isAdmin || false,
    loading: adminStatusLoading || (isAdmin && statsLoading),
    stats: stats || {
      totalUsers: 0,
      totalListings: 0,
      pendingListings: 0,
      activeListings: 0,
      closedListings: 0,
      totalApplications: 0,
      totalChatSessions: 0,
    },
    updateJobStatus,
    refreshStats,
  };
}
